import React, { useEffect } from 'react';
import Clarity from '@microsoft/clarity';
import { useSelector, useDispatch } from 'react-redux';
import { Router } from 'react-router-dom';
import history from 'src/utils/history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import DateFnsUtils from '@date-io/date-fns';
import { SnackbarProvider } from 'notistack';
import {
  createStyles,
  jssPreset,
  makeStyles,
  StylesProvider,
  ThemeProvider,
  Zoom
} from '@material-ui/core';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useJsApiLoader } from '@react-google-maps/api';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  AppInsightsContext,
  appInsightsService,
  Auth,
  CacheProvider,
  CookiesNotification,
  GoogleAnalytics,
  PORTAL_TYPE,
  Routes,
  ScrollReset,
  setAlertSettings,
  setApplicationDetails,
  setNotificationSettings,
  useInitialTheme
} from '@nsd/fe';
import apps from 'src/apps';
import i18n from 'i18next';
import Cookies from 'js-cookie';

import CollectBaseData from './components/CollectBaseData';
import DigitalDispatchTimer from './components/DigitalDispatch/DigitalDispatchTimer';
import { Five9Shell } from './components/Five9CrmAdapter';
import { setCallCenters } from './actions/callCenterActions';
import { setAllDispatchStatusAndStateMapping } from './actions/dispatchStatusAndStateMapping';
import { fetchPreferredLocationLists } from './actions/preferredLocationListsActions';

const APP_NAME = 'Dispatch Qore';
const APP_END_USER = PORTAL_TYPE.EMPLOYEE;
/* Used to determine loading/splash screen logo
Choices for APP_INTERNAL_NAME
[
  "dispatch-qore",
  "lender-qore",
  "client-qore",
  "provider-qore",
  "claims-qore",
  "contact-qore",
  "insight-qore",
  "sales-qore",
  "marketing-qore",
  "market-qore",
  "developer-qore",
  "pricebook-qore",
  "underwriter-qore",
  "integration-qore",
  "admin-qore",
  "driver-qore",
  "billing-qore",
  "agent-qore",
  "dealer-qore",
  "licensing-qore",
  "ledger-qore",
  "data-qore",
  "notifications-qore"
]
*/
const APP_INTERNAL_NAME = 'dispatch-qore';
const alertsConfigObject = {
  actionButtonContent: CancelPresentationIcon,
  // Used standard or filled
  variant: 'filled',
  position: 'top',
  transitionComponent: Zoom
};

const notificationsConfigObject = {
  pulseAnimation: true,
  displayNumberedBadge: true
};

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
const GOOGLE_SCRIPT_LIBRARIES = ['drawing', 'places'];
const GOOGLE_MAPS_STABLE_VERSION = '3.58';

const useStyles = makeStyles(() => createStyles({
  '@global': {
    '*': {
      boxSizing: 'border-box',
      margin: 0,
      padding: 0
    },
    html: {
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',
      height: '100%',
      width: '100%'
    },
    body: {
      height: '100%',
      width: '100%'
    },
    '#root': {
      height: '100%',
      width: '100%'
    }
  }
}));

function App() {
  const { user } = useSelector((state) => state.account);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      setApplicationDetails(
        APP_END_USER,
        APP_NAME,
        process.env.REACT_APP_VERSION,
        APP_INTERNAL_NAME
      )
    );
  }, [dispatch]);
  // Set alert and notification preferences / settings
  useEffect(() => {
    dispatch(setAlertSettings(alertsConfigObject));
    dispatch(setNotificationSettings(notificationsConfigObject));
    dispatch(fetchPreferredLocationLists());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      dispatch(setCallCenters(user));
      dispatch(setAllDispatchStatusAndStateMapping());
    }
  }, [dispatch, user]);

  useStyles();
  const { theme } = useInitialTheme();
  const { isLoaded } = useJsApiLoader({
    id: 'google-maps',
    /**
     * Traffic layer component issue with newer version of Google Maps Drawing API, need to downgrade
     * https://stackoverflow.com/questions/79273849/google-maps-react-component-crashes-with-trafficlayer-uncaught-error-b-369845
     */
    version: GOOGLE_MAPS_STABLE_VERSION,
    libraries: GOOGLE_SCRIPT_LIBRARIES,
    /*
     * Work around to supress Google maps loading warning in console
     * https://github.com/JustFly1984/react-google-maps-api/issues/3334
     */
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  /* initialize Application Insights
    * https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript?tabs=snippet
    * https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin
    *
    * Tracking custom telemetry events or metric is made available to all components via
    * AppInsightsContext
    */

  appInsightsService.initialize(
    process.env.REACT_APP_APPINSIGHTS_KEY,
    history,
    process.env.REACT_APP_AI_CLOUD_ROLE_NAME,
    undefined,
    undefined
  );

  if (process.env.REACT_APP_CLARITY_PROJECT_ID) {
    Clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID);
  }

  if (!theme) {
    return null;
  }
  if (!isLoaded) {
    return null;
  }

  const url = new URL(window.location.href);
  const twilio = url.searchParams.get('twilio');

  if (twilio) {
    Cookies.set('consent', 'true');
  }

  apps[0].routes.forEach((route, routeIndex) => {
    apps[0].routes[routeIndex].topBarConfig.hidden = !!twilio || url.pathname.includes('customer-dispatch-monitor');
    route.navConfig.navBarItems.forEach((navItem) => {
      navItem.subheader = i18n.t(navItem.subheader) || navItem.subheader;
      navItem.items.forEach((subItem) => {
        subItem.title = i18n.t(subItem.title) || subItem.title;
      });
    });
  });

  return (
    <AppInsightsContext.Provider value={appInsightsService.reactPlugin}>
      <CacheProvider>
        <DndProvider backend={HTML5Backend}>
          <ThemeProvider theme={theme}>
            <StylesProvider jss={jss}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <SnackbarProvider maxSnack={1}>
                  <Router history={history}>
                    {/* Above <Auth> so Five9 survives auth-guard churn; see docs/five9-integration.md §3 */}
                    <Five9Shell user={user} />
                    <Auth>
                      <DigitalDispatchTimer />
                      <CollectBaseData>
                        <ScrollReset />
                        <GoogleAnalytics />
                        <CookiesNotification />
                        <Routes
                          apps={apps || []}
                          type={APP_END_USER}
                          shouldRenderErrorBoundary
                        />
                      </CollectBaseData>
                    </Auth>
                  </Router>
                </SnackbarProvider>
              </MuiPickersUtilsProvider>
            </StylesProvider>
          </ThemeProvider>
        </DndProvider>
      </CacheProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
